import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { ScenarioAlertPreview, ScenarioDetail, ScenarioSummary } from "./types";

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["scenarios"] });

export function useScenarios() {
  return useQuery({
    queryKey: ["scenarios"],
    queryFn: async () => {
      const body = await apiFetch<{ scenarios: ScenarioSummary[] }>("/api/v1/scenarios");
      return body.scenarios;
    },
  });
}

export function useScenario(slug: string | undefined) {
  return useQuery({
    queryKey: ["scenario", slug],
    queryFn: () => apiFetch<ScenarioDetail>(`/api/v1/scenarios/${slug}`),
    enabled: !!slug,
  });
}

export function useScenarioAlertsPreview(slug: string | undefined) {
  return useQuery({
    queryKey: ["scenario-alerts-preview", slug],
    queryFn: async () => {
      const body = await apiFetch<{ alerts: ScenarioAlertPreview[] }>(
        `/api/v1/scenarios/${slug}/alerts_preview`,
      );
      return body.alerts;
    },
    enabled: !!slug,
  });
}

export function useCreateScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (yaml: string) =>
      apiFetch<{ slug: string }>("/api/v1/scenarios", { method: "POST", body: { yaml } }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateScenario(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (yaml: string) =>
      apiFetch<{ slug: string }>(`/api/v1/scenarios/${slug}`, {
        method: "PUT",
        body: { yaml },
      }),
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: ["scenario", slug] });
    },
  });
}

export function useDeleteScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<void>(`/api/v1/scenarios/${slug}`, { method: "DELETE" }),
    onSuccess: () => invalidate(qc),
  });
}

export function useResetScenarioToLibrary(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ slug: string }>(`/api/v1/scenarios/${slug}/reset`, {
        method: "POST",
      }),
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: ["scenario", slug] });
    },
  });
}
